"use client"

import React, { useState, useEffect, useMemo, useRef } from "react"
import { createClient } from "~/lib/supabase/client"
import { ALL_REGIONS, getRegionsForCountry } from "~/lib/country-regions"
import { detectRegionInLocation } from "~/lib/discover-location-regions"
import { Input } from "~/components/ui/input"
import { Button } from "~/components/ui/button"
import Image from "next/image"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu"
import { Calendar } from "~/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover"
import { format } from "date-fns"
import {
  ChevronDown,
  Search as SearchIcon,
  MapPin,
  Heart as HeartIcon,
  Sparkles as SparklesIcon,
  Users as UsersIcon,
  Calendar as CalendarIcon,
  Leaf as LeafIcon,
  Activity as ActivityIcon,
  Music as MusicIcon,
  Brain as BrainIcon,
  Wind as WindIcon,
  Waves as WavesIcon,
  Smile as SmileIcon,
  TrendingUp as TrendingUpIcon,
  Gem as GemIcon,
  Flame as FlameIcon,
  HeartHandshake as HeartHandshakeIcon,
  UserCog as UserCogIcon,
  UserCheck as UserCheckIcon,
  UserX as UserXIcon,
  Zap as ZapIcon,
  Star as StarIcon,
  HandHeart as HandHeartIcon,
  Gamepad2 as Gamepad2Icon,
  UsersRound as UsersRoundIcon,
  Palette as PaletteIcon,
  Mountain as MountainIcon,
  Tent as TentIcon,
  PartyPopper as PartyPopperIcon,
  Building2 as Building2Icon,
  Presentation as PresentationIcon,
  Image as ImageIcon,
  Scissors as ScissorsIcon,
  Workflow as WorkflowIcon,
  GraduationCap as GraduationCapIcon,
  BookOpen as BookOpenIcon,
  Pill as PillIcon,
  Briefcase as BriefcaseIcon,
  Flower2 as Flower2Icon,
  Bookmark as BookmarkIcon,
  BookmarkCheck as BookmarkCheckIcon,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import {
  loadDiscoverFilters,
  saveDiscoverFilters,
  clearSavedDiscoverFilters,
} from "~/lib/discover-filters"
import { AuthPromptDialog } from "~/components/auth/AuthPromptDialog"

const quickDateOptions = ["Today", "Tomorrow", "This Weekend", "This Week", "This Month", "Next Month"]

type DateRange = { from?: Date; to?: Date }

type SubcategoryType = {
  value: string
  label: string
  icon: LucideIcon
}

type CategoryType = {
  value: string
  label: string
  icon: LucideIcon
  subcategories: SubcategoryType[]
}

// ── Path Collective visual system ─────────────────────────────────────────────
// Yellow leads (active states), Teal and Terracotta support, Ink anchors.
const FONT_UI = "font-[family-name:var(--font-rounded)]"
const FONT_BODY = "font-[family-name:var(--font-nunito)]"
// Filter pills ("fpill"): canvas fill, parchment border, rounded-full.
const ACTIVE_FILTER = "rounded-full font-bold text-[13.5px] bg-[#F0B853] text-[#2C2D2B] border-[#D99E37] hover:bg-[#F1C577] hover:text-[#2C2D2B]"
const IDLE_FILTER = "rounded-full font-bold text-[13.5px] bg-[#F7F0E6] text-[#5E594F] border-[#D9CDB9] hover:text-[#2C2D2B] hover:border-[#8A8475] hover:bg-[#F7F0E6]"

// Example prompts under the unified search ("Try" chips in the visual system).
const TRY_PROMPTS = ["anxiety support circle", "sound healing near me", "restful, this weekend"]

// Category chip tints — terra / teal / yellow pastel fills, cycled to match
// the Collective Visual System's discover mock.
const CATCHIP_TINTS: Record<string, string> = {
  connect: "bg-[#D4D9CE] hover:shadow-[0_6px_16px_rgba(117,160,150,.28)]",
  wellbeing: "bg-[#EFD3C4] hover:shadow-[0_6px_16px_rgba(222,134,109,.22)]",
  growth: "bg-[#F3DFBE] hover:shadow-[0_6px_16px_rgba(217,158,55,.22)]",
  create: "bg-[#EFD3C4] hover:shadow-[0_6px_16px_rgba(222,134,109,.22)]",
  explore: "bg-[#D4D9CE] hover:shadow-[0_6px_16px_rgba(117,160,150,.28)]",
}

const subcategoryIconMap: Record<string, LucideIcon> = {
  // Connect
  "Community Gatherings": UsersRoundIcon,
  "Run Clubs": ActivityIcon,
  "Social Experiences": Gamepad2Icon,
  "Community Dinners": UsersIcon,
  "Networking": HeartHandshakeIcon,
  "Dating & Relationships": HandHeartIcon,
  "Men's Circles": UserCheckIcon,
  "Women's Circles": UserXIcon,
  "Support Groups": HandHeartIcon,
  // Wellbeing
  "Yoga": ActivityIcon,
  "Fitness": ZapIcon,
  "Pilates": ActivityIcon,
  "Breathwork": WindIcon,
  "Meditation & Mindfulness": BrainIcon,
  "Nervous System Regulation": WavesIcon,
  "Mobility & Recovery": SmileIcon,
  "Somatic Therapy": ActivityIcon,
  "Psychotherapy": BrainIcon,
  "Bodywork": ScissorsIcon,
  "Trauma Healing": HandHeartIcon,
  "Natural Medicine": PillIcon,
  "Alternative Therapies": Flower2Icon,
  // Growth
  "Coaching": GraduationCapIcon,
  "Personal Growth": TrendingUpIcon,
  "Courses & Training": BookOpenIcon,
  "Workshops": BriefcaseIcon,
  "Therapeutic Workshops": BriefcaseIcon,
  "Spiritual Guidance": UserCogIcon,
  "Energy Work": ZapIcon,
  "Astrology": StarIcon,
  "Ceremonies": GemIcon,
  // Create
  "Art": PaletteIcon,
  "Music": MusicIcon,
  "Cooking": FlameIcon,
  "Gardening": LeafIcon,
  "Writing": BookOpenIcon,
  "Photography": ImageIcon,
  "Film & Content Creation": ImageIcon,
  "Dance": MusicIcon,
  "Craft & Makers": ScissorsIcon,
  "Pottery & Ceramics": GemIcon,
  "Floral Design": Flower2Icon,
  "Sewing & Textiles": ScissorsIcon,
  "DIY & Woodworking": WorkflowIcon,
  "Entrepreneurship": TrendingUpIcon,
  "Business Workshops": BriefcaseIcon,
  "Public Speaking": PresentationIcon,
  "Creative Communities": UsersRoundIcon,
  // Explore
  "Retreats": MountainIcon,
  "Festivals": PartyPopperIcon,
  "Nature Experiences": TentIcon,
  "Travel Experiences": MountainIcon,
  "Immersive Experiences": SparklesIcon,
  "Celebrations": PartyPopperIcon,
  "Summits & Expos": Building2Icon,
  "Wellness Summits": PresentationIcon,
}

const createSubcategory = (label: string): SubcategoryType => ({
  value: label.toLowerCase().replace(/\s+/g, "-"),
  label,
  icon: subcategoryIconMap[label] || ActivityIcon,
})

// Path Collective taxonomy — mirrors the API's CATEGORIZATION_MAP.
const categories: CategoryType[] = [
  {
    value: "connect",
    label: "Connect",
    icon: UsersIcon,
    subcategories: [
      "Community Gatherings",
      "Run Clubs",
      "Social Experiences",
      "Community Dinners",
      "Networking",
      "Dating & Relationships",
      "Men's Circles",
      "Women's Circles",
      "Support Groups",
    ].map(createSubcategory),
  },
  {
    value: "wellbeing",
    label: "Wellbeing",
    icon: HeartIcon,
    subcategories: [
      "Yoga",
      "Fitness",
      "Pilates",
      "Breathwork",
      "Meditation & Mindfulness",
      "Nervous System Regulation",
      "Mobility & Recovery",
      "Somatic Therapy",
      "Psychotherapy",
      "Bodywork",
      "Trauma Healing",
      "Natural Medicine",
      "Alternative Therapies",
    ].map(createSubcategory),
  },
  {
    value: "growth",
    label: "Growth",
    icon: SparklesIcon,
    subcategories: [
      "Coaching",
      "Personal Growth",
      "Courses & Training",
      "Workshops",
      "Therapeutic Workshops",
      "Spiritual Guidance",
      "Energy Work",
      "Astrology",
      "Ceremonies",
    ].map(createSubcategory),
  },
  {
    value: "create",
    label: "Create",
    icon: LeafIcon,
    subcategories: [
      "Art",
      "Music",
      "Cooking",
      "Gardening",
      "Writing",
      "Photography",
      "Film & Content Creation",
      "Dance",
      "Craft & Makers",
      "Pottery & Ceramics",
      "Floral Design",
      "Sewing & Textiles",
      "DIY & Woodworking",
      "Entrepreneurship",
      "Business Workshops",
      "Public Speaking",
      "Creative Communities",
    ].map(createSubcategory),
  },
  {
    value: "explore",
    label: "Explore",
    icon: CalendarIcon,
    subcategories: [
      "Retreats",
      "Festivals",
      "Nature Experiences",
      "Travel Experiences",
      "Immersive Experiences",
      "Celebrations",
      "Summits & Expos",
      "Wellness Summits",
    ].map(createSubcategory),
  },
]

const priceOptions: string[] = ["Free", "Paid", "Donation"]
const formatOptions = ["In-person", "Online"]

const makeBlankFilters = () => ({
  date: "",
  subcategories: [] as string[],
  prices: [...priceOptions],
  facilitator: "",
  smartPrompt: "",
  format: [...formatOptions],
  location: [] as string[],
  suburb: "",
})

type Facilitator = { user_id: string; full_name: string; avatar_url: string | null }

type Event = {
  location?: string | null
  owner?: { user_id: string; full_name: string; avatar_url: string | null }
  hosts?: Array<{ user_id: string; full_name: string; avatar_url: string | null }>
}

// #1392 — was Australia-only (AUSTRALIAN_STATES); the full cross-country
// region list now drives both the geo-detect check and the discovery
// dropdown's ordering, via detectRegionInLocation (lib/discover-location-regions.ts).
// STATE_ABBR is still AU-only: it's a display abbreviation, and there's no
// established abbreviation convention for the other offered countries.
const STATE_ABBR: Record<string, string> = {
  'New South Wales': 'NSW', 'Victoria': 'VIC', 'Queensland': 'QLD',
  'South Australia': 'SA', 'Western Australia': 'WA', 'Tasmania': 'TAS',
  'Northern Territory': 'NT', 'Australian Capital Territory': 'ACT',
}

// ipapi.co's ISO 3166-1 alpha-2 country_code → the country names
// country-regions.ts keys its region lists by.
const IP_GEO_COUNTRY_CODE_TO_NAME: Record<string, string> = {
  AU: 'Australia', NZ: 'New Zealand', US: 'United States', GB: 'United Kingdom', CA: 'Canada',
}

export default function EventsFilterBar({
  onFilterChange,
  events = [],
  persistFilters = true,
  resetSignal = 0,
}: {
  onFilterChange: (filters: any) => void
  events?: Event[]
  // The admin dashboard reuses this bar; it must not restore or overwrite the
  // member's saved discover filters (shared localStorage key).
  persistFilters?: boolean
  // #1179 — bumped by the parent's "Clear filters" button; same as Reset here.
  resetSignal?: number
}) {
  const [filters, setFilters] = useState(makeBlankFilters)

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showFacilitatorSuggestions, setShowFacilitatorSuggestions] = useState(false)
  const [filtersSaved, setFiltersSaved] = useState(false)
  // Saving filters is a member feature: visitors get the same sign-up prompt as
  // saving an event. null = auth state not yet resolved.
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null)
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)

  // Date picker state
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [rangeCalendarOpen, setRangeCalendarOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)
  const [showfilters, setShowfilters] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isOpeningCalendar, setIsOpeningCalendar] = useState(false)
  const [isOpeningRange, setIsOpeningRange] = useState(false)

  const [apiFacilitators, setApiFacilitators] = useState<Facilitator[]>([])

  const facilitatorInputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fetch approved facilitators from API (once on mount).
  // The endpoint requires auth; visitors fall back to facilitators derived from events.
  useEffect(() => {
    const fetchFacilitators = async () => {
      try {
        const supabase = createClient()
        const { data: sessionData } = await supabase.auth.getSession()
        const accessToken = sessionData?.session?.access_token
        if (!accessToken) return
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/user/facilitators`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        if (!res.ok) return
        const data = await res.json() as Facilitator[]
        setApiFacilitators(data)
      } catch {
        // Non-critical — facilitator list from events still works
      }
    }
    void fetchFacilitators()
  }, [])

  // Restore saved filters if present; otherwise fall back to best-effort
  // IP-geo state detection (once on mount). A saved set always wins so a
  // slow geo lookup can't clobber the user's restored filters.
  useEffect(() => {
    const saved = persistFilters ? loadDiscoverFilters() : null
    if (saved) {
      const restored = { ...makeBlankFilters(), ...saved }
      setFilters(restored)
      onFilterChange(restored)
      return
    }
    const detectLocation = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/")
        if (!res.ok) return
        const data = await res.json() as { country_code?: string; region?: string }
        // #1392 — was AU-only; now checks the visitor's region against
        // whichever of the offered countries ipapi reports them in.
        const countryName = IP_GEO_COUNTRY_CODE_TO_NAME[data.country_code ?? ""]
        if (!countryName) return
        const region = data.region ?? ""
        if (getRegionsForCountry(countryName).includes(region)) {
          const newFilters = { ...filters, location: [region] }
          setFilters(newFilters)
          onFilterChange(newFilters)
        }
      } catch {
        // Silently fail — geo detection is best-effort
      }
    }
    void detectLocation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Track auth state so "Save Filters" can prompt visitors to sign up.
  useEffect(() => {
    const supabase = createClient()
    void supabase.auth.getSession().then(({ data }) => setIsAuthed(!!data.session?.access_token))
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) =>
      setIsAuthed(!!session?.access_token),
    )
    return () => sub.subscription.unsubscribe()
  }, [])

  // Sync selectedDate / dateRange when filters.date changes externally (e.g. reset)
  useEffect(() => {
    if (filters.date && filters.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setSelectedDate(new Date(filters.date))
      setDateRange(undefined)
    } else if (filters.date && filters.date.includes(":")) {
      const [fromStr, toStr] = filters.date.split(":")
      setDateRange({
        from: fromStr ? new Date(fromStr) : undefined,
        to: toStr ? new Date(toStr) : undefined,
      })
      setSelectedDate(undefined)
    } else {
      setSelectedDate(undefined)
      setDateRange(undefined)
    }
  }, [filters.date])

  const allFacilitators = useMemo(() => {
    const map = new Map<string, Facilitator>()
    apiFacilitators.forEach((f) => map.set(f.user_id, f))
    events.forEach((event) => {
      if (event.owner?.full_name) map.set(event.owner.user_id, event.owner as Facilitator)
      event.hosts?.forEach((h) => { if (h.full_name) map.set(h.user_id, h as Facilitator) })
    })
    return Array.from(map.values()).sort((a, b) => a.full_name.localeCompare(b.full_name))
  }, [events, apiFacilitators])

  const facilitatorSuggestions = useMemo(() => {
    const term = filters.facilitator.toLowerCase().trim()
    if (!term) return allFacilitators
    return allFacilitators.filter((f) => f.full_name.toLowerCase().includes(term))
  }, [filters.facilitator, allFacilitators])

  // #1392 — was AU-only (AUSTRALIAN_STATES + an inline AU abbreviation
  // regex); detectRegionInLocation recognises every offered country's
  // regions, AU abbreviations included, and ALL_REGIONS keeps the dropdown's
  // ordering stable across all of them.
  const allLocations = useMemo(() => {
    const stateSet = new Set<string>()
    events.forEach((event) => {
      if (!event.location?.trim() || event.location.toLowerCase() === 'online') return
      const region = detectRegionInLocation(event.location)
      if (region) stateSet.add(region)
    })
    return ALL_REGIONS.filter((s) => stateSet.has(s))
  }, [events])

  // Suburb autocomplete candidates, derived from event location strings
  // (the segment immediately before the state abbreviation).
  const allSuburbs = useMemo(() => {
    const set = new Set<string>()
    events.forEach((event) => {
      const loc = event.location
      if (!loc?.trim() || loc.toLowerCase() === 'online') return
      const match = loc.match(/([^,]+?),?\s+\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i)
      const candidate = match?.[1]?.trim()
      if (candidate && candidate.length >= 3 && !/\d/.test(candidate)) set.add(candidate)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [events])

  // Close facilitator dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        facilitatorInputRef.current && !facilitatorInputRef.current.contains(e.target as Node) &&
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)
      ) setShowFacilitatorSuggestions(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const updateFilter = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value }
    setFilters(newFilters)
    onFilterChange(newFilters)
  }

  // ── Date handlers ──────────────────────────────────────────────────────────

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date)
      setDateRange(undefined)
      updateFilter("date", format(date, "yyyy-MM-dd"))
      setCalendarOpen(false)
    }
  }

  const handleRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range)
    if (range?.from && range?.to) {
      updateFilter("date", `${format(range.from, "yyyy-MM-dd")}:${format(range.to, "yyyy-MM-dd")}`)
      setRangeCalendarOpen(false)
    } else if (range?.from) {
      // Partial range — update display but don't close yet
      updateFilter("date", `${format(range.from, "yyyy-MM-dd")}:`)
    }
  }

  const handleDateOptionSelect = (value: string) => {
    setSelectedDate(undefined)
    setDateRange(undefined)
    updateFilter("date", value)
  }

  const handlePickDateClick = () => {
    setIsOpeningCalendar(true)
    setDropdownOpen(false)
    setTimeout(() => { setCalendarOpen(true); setIsOpeningCalendar(false) }, 200)
  }

  const handlePickRangeClick = () => {
    setIsOpeningRange(true)
    setDropdownOpen(false)
    setTimeout(() => { setRangeCalendarOpen(true); setIsOpeningRange(false) }, 200)
  }

  const getDateDisplayText = () => {
    if (!filters.date) return "All dates"
    if (filters.date.includes(":")) {
      const [fromStr, toStr] = filters.date.split(":")
      if (fromStr && toStr) {
        return `${format(new Date(fromStr), "MMM d")} – ${format(new Date(toStr), "MMM d, yyyy")}`
      }
      if (fromStr) return `From ${format(new Date(fromStr), "MMM d")}`
    }
    if (filters.date.match(/^\d{4}-\d{2}-\d{2}$/)) return format(new Date(filters.date), "MMM d, yyyy")
    return filters.date
  }

  // ── Category / subcategory handlers ───────────────────────────────────────

  // Selecting a category is exclusive: it selects ALL of that category's
  // subcategories and clears any selection from other categories. 
  // Re clicking category deselects current category
  const handleCategoryClick = (categoryValue: string) => {
    if (selectedCategory === categoryValue) {
      setSelectedCategory(null);
      updateFilter("subcategories", []);
      return;
    }
    const category = categories.find((c) => c.value === categoryValue)
    if (!category) return
    
    const categoryLabels = category.subcategories.map((s) => s.label)
    setSelectedCategory(categoryValue)
    updateFilter("subcategories", categoryLabels)
  }

  // multiple subcategories can be selected:
  // if all subcats are present, remove all but selected subcat from array
  // else the existing subcat is selected, remove current subcat from array
  // else subcat is not present , add the selected subcat to array
  const handleSubcategoryClick = (label: string) => {
    const current = filters.subcategories
    let updated : string[]
    const allSelected = selectedCategoryData && current.length === selectedCategoryData.subcategories.length;
    
    if (allSelected) {
    updateFilter("subcategories", [label])
    return
    }
    else if(current.includes(label)){
      updated = current.filter((l) => l !== label);
    }
    else {
      updated = [...current, label];
    }
    updateFilter("subcategories", updated)
 
  }

  // "Reset" — resets only the category/subcategory filter (no category filter =
  // everything shown). Leaves the other filter pills (date, price, …) intact.
  const clearCategoryFilters = () => {
    setSelectedCategory(null)
    updateFilter("subcategories", [])
  }
  // "Show all" resets subcategories to contain all possible subcats 
  const showAllSubcategories = () => {
    if (!selectedCategoryData) return;
    const allLabels = selectedCategoryData.subcategories.map((s) => s.label);
    updateFilter("subcategories", allLabels);
  }

  // ── Price handlers ─────────────────────────────────────────────────────────

  const handlePriceToggle = (price: string) => {
    const updated = filters.prices.includes(price)
      ? filters.prices.filter((p) => p !== price)
      : [...filters.prices, price]
    updateFilter("prices", updated)
  }

  // ── Location handlers ──────────────────────────────────────────────────────

  const handleLocationToggle = (state: string) => {
    const updated = filters.location.includes(state)
      ? filters.location.filter((s) => s !== state)
      : [...filters.location, state]
    updateFilter("location", updated)
  }

  // Hanlde show filters
  const handleshowfilters = () => {
    setShowfilters(!showfilters)
  }

  // ── Save / reset ───────────────────────────────────────────────────────────

  const handleSaveFilters = () => {
    // Saving filters is a member feature — prompt visitors to sign up, mirroring
    // the save-an-event flow. (null auth state = still resolving; let it through.)
    if (isAuthed === false) {
      setShowAuthPrompt(true)
      return
    }
    saveDiscoverFilters({
      date: filters.date,
      subcategories: filters.subcategories,
      prices: filters.prices,
      format: filters.format,
      location: filters.location,
      suburb: filters.suburb,
    })
    setFiltersSaved(true)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    savedTimerRef.current = setTimeout(() => setFiltersSaved(false), 2000)
  }

  const resetAllFilters = () => {
    const blank = makeBlankFilters()
    setFilters(blank)
    setSelectedCategory(null)
    setSelectedDate(undefined)
    setDateRange(undefined)
    if (persistFilters) clearSavedDiscoverFilters()
    onFilterChange(blank)
  }

  useEffect(() => {
    if (resetSignal > 0) resetAllFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal])

  const selectedCategoryData = selectedCategory
    ? categories.find((c) => c.value === selectedCategory)
    : null

  const isCalendarAnyOpen = calendarOpen || rangeCalendarOpen

  const locationLabel =
    filters.location.length === 0
      ? "All states"
      : filters.location.length === 1
        ? (STATE_ABBR[filters.location[0]!] ?? filters.location[0])
        : `${filters.location.length} states`

  return (
    <div className="space-y-4 mb-6">
      {/* Button to show and hide filters*/}
      <div className="block sm:hidden">
        <Button
          onClick={handleshowfilters}
          className={`${FONT_UI} rounded-full font-bold bg-[#F0B853] text-[#2C2D2B] hover:bg-[#F1C577] shadow-[inset_0_0_0_1.5px_#D99E37]`}
        >
          {showfilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </div>

      {/* Find panel — unified search + suggestion chips + filter pills */}
      <div className={`${showfilters ? "block" : "hidden"} sm:block bg-[#FFFDF8] border border-[#EFE7D7] rounded-[20px] px-5 py-5 sm:px-6 shadow-[0_8px_28px_rgba(44,45,43,.05)]`}>

        {/* UNIFIED AI SEARCH — scores events by prompt tokens */}
        <div className="flex items-center gap-2.5 bg-[#F7F0E6] border border-[#D9CDB9] rounded-full pl-4 pr-1.5 py-1.5 w-full transition-all focus-within:border-[#75A096] focus-within:ring-[3px] focus-within:ring-[#75A096]/20">
          <SearchIcon className="h-5 w-5 text-[#8A8475] flex-none" />
          <input
            aria-label="Search events"
            placeholder="Search events, facilitators or places"
            value={filters.smartPrompt ?? ""}
            onChange={(e) => updateFilter("smartPrompt", e.target.value)}
            className={`flex-1 min-w-0 border-0 bg-transparent outline-none text-[15px] text-[#2C2D2B] placeholder:text-[#8A8475] py-2 ${FONT_BODY}`}
          />
          <button
            type="button"
            onClick={() => onFilterChange({ ...filters })}
            className={`${FONT_UI} flex-none font-bold text-sm text-[#2C2D2B] bg-[#F0B853] rounded-full px-5 py-2.5 shadow-[inset_0_0_0_1.5px_#D99E37] transition-all hover:-translate-y-px hover:shadow-[inset_0_0_0_1.5px_#D99E37,0_6px_16px_rgba(217,158,55,.26)]`}
          >
            Search
          </button>
        </div>

        {/* Try chips */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className={`${FONT_BODY} inline-flex items-center gap-1.5 text-xs font-bold tracking-[.03em] text-[#8A8475]`}>
            <SparklesIcon className="h-3.5 w-3.5 text-[#75A096]" /> Try
          </span>
          {TRY_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => updateFilter("smartPrompt", filters.smartPrompt === p ? "" : p)}
              className={`${FONT_UI} font-bold text-[13px] rounded-full px-3.5 py-1.5 border transition-colors ${
                filters.smartPrompt === p
                  ? "border-[#75A096] bg-[#75A096]/20 text-[#2F5348]"
                  : "border-[#75A096] bg-[#75A096]/10 text-[#3f6a5e] hover:bg-[#75A096]/[.18]"
              }`}
            >
              {p}
            </button>
          ))}
        </div>

        <div className="h-px bg-[#EFE7D7] -mx-5 sm:-mx-6 my-4" />

        {/* Filter pill row */}
        <div className="flex flex-wrap gap-2.5 items-center">

        {/* DATE */}
        <Popover
          open={isCalendarAnyOpen}
          onOpenChange={(open) => {
            if (!open && !isOpeningCalendar && !isOpeningRange) {
              setCalendarOpen(false)
              setRangeCalendarOpen(false)
            }
          }}
        >
          <DropdownMenu
            open={dropdownOpen && !isCalendarAnyOpen}
            onOpenChange={(open) => {
              if (!isCalendarAnyOpen && !isOpeningCalendar && !isOpeningRange) setDropdownOpen(open)
            }}
          >
            <DropdownMenuTrigger asChild>
              <PopoverTrigger asChild>
                <Button variant="outline" className={`${FONT_UI} ${filters.date ? ACTIVE_FILTER : IDLE_FILTER}`}>
                  {getDateDisplayText()} <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </PopoverTrigger>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-48"
              onPointerDownOutside={(e) => { if (isOpeningCalendar || isOpeningRange) e.preventDefault() }}
            >
              <DropdownMenuRadioGroup
                value={
                  filters.date &&
                    !filters.date.match(/^\d{4}-\d{2}-\d{2}$/) &&
                    !filters.date.includes(":")
                    ? filters.date
                    : ""
                }
                onValueChange={handleDateOptionSelect}
              >
                <DropdownMenuRadioItem value="" className="text-[#2C2D2B]">All dates</DropdownMenuRadioItem>
                {quickDateOptions.map((d) => (
                  <DropdownMenuRadioItem key={d} value={d} className="text-[#2C2D2B]">{d}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <div className="py-1 px-1 flex flex-col gap-1">
                <button
                  className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-[#F7F0E6] text-[#2C2D2B]"
                  onClick={(e) => { e.preventDefault(); handlePickDateClick() }}
                >
                  Pick a date
                </button>
                <button
                  className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-[#F7F0E6] text-[#2C2D2B]"
                  onClick={(e) => { e.preventDefault(); handlePickRangeClick() }}
                >
                  Pick a range
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <PopoverContent className="w-auto p-0 z-[100]" align="start">
            {calendarOpen && (
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            )}
            {rangeCalendarOpen && (
              <Calendar
                mode="range"
                selected={dateRange as any}
                onSelect={(range: any) => handleRangeSelect(range as DateRange)}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                numberOfMonths={2}
                initialFocus
              />
            )}
          </PopoverContent>
        </Popover>

        {/* LOCATION — states */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={`${FONT_UI} ${filters.location.length > 0 ? ACTIVE_FILTER : IDLE_FILTER}`}
            >
              <MapPin className="mr-2 h-4 w-4" />
              {locationLabel}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60">
            <DropdownMenuCheckboxItem
              checked={filters.location.length === 0}
              onCheckedChange={() => updateFilter("location", [])}
              onSelect={(e) => e.preventDefault()}
              className="text-[#2C2D2B] font-medium"
            >
              All states
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            {allLocations.map((state) => (
              <DropdownMenuCheckboxItem
                key={state}
                checked={filters.location.includes(state)}
                onCheckedChange={() => handleLocationToggle(state)}
                onSelect={(e) => e.preventDefault()}
                className="text-[#2C2D2B]"
              >
                {state}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* SUBURB — standalone next to the state filter. Deliberately NOT
            inside the dropdown: Radix menus steal focus from embedded inputs
            on pointer-move and reroute keystrokes into typeahead/selection. */}
        <div className="relative w-36">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A8475] pointer-events-none" />
          <Input
            aria-label="Filter by suburb"
            list="suburb-suggestions"
            placeholder="Suburb"
            value={filters.suburb}
            onChange={(e) => updateFilter("suburb", e.target.value)}
            className={`${FONT_UI} w-full pl-9 rounded-full font-bold text-[13.5px] placeholder:text-[#8A8475] placeholder:font-bold focus-visible:border-[#75A096] focus-visible:ring-[#75A096]/40 ${
              filters.suburb.trim() ? "bg-[#F0B853]/20 border-[#D99E37] text-[#2C2D2B]" : "bg-[#F7F0E6] border-[#D9CDB9] text-[#5E594F]"
            }`}
          />
          <datalist id="suburb-suggestions">
            {allSuburbs.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>

        {/* TYPE — single button, multi-select dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={`${FONT_UI} ${filters.prices.length < 3 ? ACTIVE_FILTER : IDLE_FILTER}`}
            >
              {filters.prices.length === 0
                ? "Type"
                : filters.prices.length === 3
                  ? "All types"
                  : filters.prices.join(", ")}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-40">
            {priceOptions.map((p) => (
              <DropdownMenuCheckboxItem
                key={p}
                checked={filters.prices.includes(p)}
                onCheckedChange={() => handlePriceToggle(p)}
                onSelect={(e) => e.preventDefault()}
                className="text-[#2C2D2B]"
              >
                {p}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* FORMAT — single button, multi-select dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={`${FONT_UI} ${filters.format.length < 2 ? ACTIVE_FILTER : IDLE_FILTER}`}
            >
              {filters.format.length === 0
                ? "Format"
                : filters.format.length === 2
                  ? "All formats"
                  : filters.format[0]}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-40">
            {formatOptions.map((f) => (
              <DropdownMenuCheckboxItem
                key={f}
                checked={filters.format.includes(f)}
                onCheckedChange={() => {
                  const updated = filters.format.includes(f)
                    ? filters.format.filter((v: string) => v !== f)
                    : [...filters.format, f]
                  updateFilter("format", updated)
                }}
                onSelect={(e) => e.preventDefault()}
                className="text-[#2C2D2B]"
              >
                {f}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* FACILITATOR */}
        <div className="relative w-56">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5E5D59] pointer-events-none" />
            <Input
              ref={facilitatorInputRef}
              placeholder="Search facilitator..."
              value={filters.facilitator}
              onChange={(e) => { updateFilter("facilitator", e.target.value); setShowFacilitatorSuggestions(true) }}
              onFocus={() => setShowFacilitatorSuggestions(true)}
              className={`${FONT_UI} w-full pl-10 rounded-full font-bold text-[13.5px] placeholder:text-[#8A8475] placeholder:font-bold focus-visible:border-[#75A096] focus-visible:ring-[#75A096]/40 ${
                filters.facilitator.trim() ? "bg-[#F0B853]/20 border-[#D99E37] text-[#2C2D2B]" : "bg-[#F7F0E6] border-[#D9CDB9] text-[#5E594F]"
              }`}
            />
          </div>
          {showFacilitatorSuggestions && facilitatorSuggestions.length > 0 && (
            <div ref={suggestionsRef} className="absolute z-50 w-full mt-1 bg-[#FFFDF8] border border-[#2C2D2B]/10 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {facilitatorSuggestions.map((f) => (
                <button
                  key={f.user_id}
                  type="button"
                  onClick={() => { updateFilter("facilitator", f.full_name); setShowFacilitatorSuggestions(false) }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F7F0E6] transition-colors text-left"
                >
                  {f.avatar_url ? (
                    <div className="relative w-10 h-10 flex-shrink-0">
                      <Image src={f.avatar_url} alt={f.full_name} fill className="rounded-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-[#F2D29A] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2C2D2B] font-medium text-sm">{f.full_name.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                  <span className="text-sm font-medium text-[#2C2D2B]">{f.full_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* SAVE FILTERS */}
        {persistFilters && (
          <Button
            variant="outline"
            onClick={handleSaveFilters}
            className={`${FONT_UI} whitespace-nowrap rounded-full font-bold text-[13.5px] border-[#75A096]/60 text-[#4E7A6F] bg-[#FFFDF8] hover:bg-[#75A096]/10 hover:text-[#3E6459]`}
          >
            {filtersSaved ? (
              <>
                <BookmarkCheckIcon className="h-4 w-4" /> Saved
              </>
            ) : (
              <>
                <BookmarkIcon className="h-4 w-4" /> Save Filters
              </>
            )}
          </Button>
        )}

        {/* RESET ALL — terra text button, pushed to the row's end */}
        <button
          type="button"
          onClick={resetAllFilters}
          className={`${FONT_UI} ml-auto font-bold text-[13.5px] text-[#DE866D] hover:underline whitespace-nowrap px-1.5 py-2`}
        >
          Reset all
        </button>
        </div>
      </div>

      {/* Browse by category — flat tinted chips (Collective Visual System) */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className={`${FONT_UI} font-bold text-sm text-[#2C2D2B]`}>Browse by category</div>
          {/* "All" — resets only the category/subcategory filter (same terra
              styling as "Reset all"), leaving date/price/format/etc. intact. */}
          {selectedCategory && (
          <button
            type="button"
            onClick={clearCategoryFilters}
            className={`${FONT_UI} font-bold text-[13.5px] text-[#DE866D] hover:underline whitespace-nowrap px-1.5 py-1`}
          >
            Reset
          </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2.5">
          {categories.map((category) => {
            const isOpen = selectedCategory === category.value
            const selectedSubCount = category.subcategories.filter((s) => filters.subcategories.includes(s.label)).length
            const isActive = isOpen || selectedSubCount > 0
            const tint = CATCHIP_TINTS[category.value] ?? "bg-[#F3DFBE]"

            return (
              <button
                key={category.value}
                onClick={() => handleCategoryClick(category.value)}
                className={`${FONT_UI} relative font-bold text-[13.5px] text-[#2C2D2B] rounded-full px-4 py-2 border transition-all hover:-translate-y-px ${tint} ${
                  isActive ? "border-[#2C2D2B]/30 shadow-sm" : "border-transparent"
                }`}
              >
                {category.label}
                {selectedSubCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#DE866D] text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-[#F7F0E6]">
                    {selectedSubCount}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Subcategory chips, shown when a category is selected.
          #1588: this block used to be `hidden sm:block`, so on a phone in
          portrait (390px, below Tailwind's 640px `sm`) the chips did not
          render at all and only turned up after rotating to landscape. The
          category chips above are always visible, so this row is too; it
          wraps (flex-wrap) and so never needs horizontal scrolling. */}
      {
        selectedCategoryData && (
          <div data-testid="discover-subcategories" className="block pb-2 pt-2 border-t border-[#2C2D2B]/10">
            <div className="flex flex-wrap gap-2 items-center">
              {selectedCategoryData.subcategories.map((sub) => {
                const SubIcon = sub.icon
                const isSelected = filters.subcategories.includes(sub.label)
                return (
                  <Button
                    key={sub.value}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSubcategoryClick(sub.label)}
                    className={`flex items-center gap-2 whitespace-nowrap rounded-full ${FONT_UI} ${isSelected ? ACTIVE_FILTER : IDLE_FILTER}`}
                  >
                    <SubIcon className="h-3.5 w-3.5" />
                    {sub.label}
                  </Button>
                )
              })}
            </div>
              {/* Show All button: only displayed once user unselects a subcat */}
              {filters.subcategories.length < selectedCategoryData.subcategories.length && (
                <div className="flex justify-start mt-2">
                  <button
                    type="button"
                    onClick={showAllSubcategories}
                    className={`${FONT_UI} font-bold text-[13.5px] text-[#DE866D] hover:underline whitespace-nowrap px-1.5 py-1`}
                  >
                    Show All
                  </button>
                </div>
              )}
          </div>
        )
      }

      <AuthPromptDialog
        open={showAuthPrompt}
        onOpenChange={setShowAuthPrompt}
        title="Save your filters"
        description="Create your free account to save your discovery filters and pick up right where you left off."
        icon="🔖"
      />
    </div >
  )
}
